import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { API } from '../config';

/**
 * Pago de Celula con USDT TRC-20.
 * Flujo: usuario elige nivel -> backend crea orden -> muestra direccion + monto + memo
 * + QR + countdown -> poll status cada 5s -> al confirmarse, refresca.
 */
const UsdtPaymentPanel = ({ levels, onSuccess }) => {
  const [config, setConfig] = useState(null);
  const [selected, setSelected] = useState(null);
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/usdt/config`).then(r => setConfig(r.data)).catch(() => setConfig({ enabled: false }));
  }, []);

  const createOrder = async (levelId) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/usdt/order`, { level: levelId });
      setOrder(res.data);
      setStatus({ status: 'pending' });
      pollStatus(res.data.order_id);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error creando orden');
    } finally {
      setLoading(false);
    }
  };

  const pollStatus = (orderId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/usdt/order/${orderId}/status`);
        setStatus(res.data);
        if (res.data.status === 'confirmed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (onSuccess) onSuccess(res.data);
        } else if (res.data.status === 'expired') {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (e) { /* ignore */ }
    }, 5000);
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const copy = (text) => navigator.clipboard?.writeText(text);

  if (!config) return null;
  if (!config.enabled) {
    return (
      <div data-testid="usdt-disabled" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        Pago USDT TRC-20 esta deshabilitado temporalmente. Usa Stripe por ahora.
      </div>
    );
  }

  if (order) {
    const isConfirmed = status?.status === 'confirmed';
    const isExpired = status?.status === 'expired';
    const isConfirming = status?.status === 'confirming';
    return (
      <div className="rounded-2xl border-2 border-emerald-200 bg-white p-5 space-y-4" data-testid="usdt-order-panel">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Pago USDT TRC-20</h3>
            <p className="text-xs text-gray-500">Red TRON · {order.network}</p>
          </div>
          {isConfirmed && <span className="px-3 py-1 rounded-full bg-green-600 text-white text-xs font-bold" data-testid="usdt-status-confirmed">PAGADO</span>}
          {isConfirming && <span className="px-3 py-1 rounded-full bg-amber-600 text-white text-xs font-bold animate-pulse">DETECTADO</span>}
          {isExpired && <span className="px-3 py-1 rounded-full bg-red-600 text-white text-xs font-bold">EXPIRADA</span>}
          {!status?.status || status.status === 'pending' ? (
            <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold animate-pulse" data-testid="usdt-status-pending">ESPERANDO PAGO</span>
          ) : null}
        </div>

        {!isConfirmed && !isExpired && (
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="flex flex-col items-center bg-gray-50 rounded-xl p-4">
              <div data-testid="usdt-qr" className="bg-white p-2 rounded">
                <QRCodeSVG value={`tron:${order.receiver_address}?amount=${order.amount_usdt}&token=USDT`} size={180} level="M" />
              </div>
              <p className="text-[11px] text-gray-500 mt-2 text-center">Escanea con tu wallet TRON</p>
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-[11px] text-gray-500 mb-1">DIRECCION DE PAGO</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-[11px] bg-gray-100 px-2 py-1.5 rounded break-all" data-testid="usdt-address">{order.receiver_address}</code>
                  <button onClick={() => copy(order.receiver_address)} className="text-xs px-2 py-1 bg-blue-600 text-white rounded">Copiar</button>
                </div>
              </div>
              <div>
                <div className="text-[11px] text-gray-500 mb-1">MONTO EXACTO</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 font-bold text-lg bg-gray-100 px-2 py-1.5 rounded" data-testid="usdt-amount">{order.amount_usdt} USDT</code>
                  <button onClick={() => copy(String(order.amount_usdt))} className="text-xs px-2 py-1 bg-blue-600 text-white rounded">Copiar</button>
                </div>
              </div>
              <div>
                <div className="text-[11px] text-gray-500 mb-1">MEMO (opcional)</div>
                <code className="block text-[11px] bg-gray-100 px-2 py-1.5 rounded">{order.memo}</code>
              </div>
              <div className="text-[11px] text-gray-600 bg-amber-50 border border-amber-200 rounded p-2">
                {'\u26A0'} Envia EXACTAMENTE {order.amount_usdt} USDT, ni mas ni menos. La activacion es automatica.
              </div>
            </div>
          </div>
        )}

        {isConfirmed && (
          <div className="bg-green-50 border border-green-300 rounded-xl p-4 text-center" data-testid="usdt-success">
            <div className="text-3xl mb-2">{'\u2713'}</div>
            <p className="font-bold text-green-800">Celula activada</p>
            <p className="text-xs text-green-700 mt-1">Nivel {order.cualcoin_to_receive} Cc + {order.pv_breakdown?.net_usd} PV netos</p>
            <p className="text-[10px] text-gray-500 mt-2 break-all">tx: {status?.tx_hash}</p>
          </div>
        )}

        {isExpired && (
          <div className="bg-red-50 border border-red-300 rounded-xl p-4 text-center">
            <p className="text-red-800 font-semibold">Ventana de pago expirada</p>
            <button onClick={() => { setOrder(null); setStatus(null); }} className="mt-2 px-4 py-1.5 rounded bg-red-600 text-white text-sm">Crear nueva orden</button>
          </div>
        )}

        <div className="border-t border-gray-100 pt-3 text-[11px] text-gray-500 space-y-0.5">
          <div className="flex justify-between"><span>Bruto:</span><span className="font-mono">${order.pv_breakdown.gross_usd}</span></div>
          <div className="flex justify-between"><span>Fee red TRON:</span><span className="font-mono">-${order.pv_breakdown.processor_fee_usd}</span></div>
          <div className="flex justify-between font-bold text-gray-800"><span>PV netos:</span><span className="font-mono">{order.pv_breakdown.net_usd} PV</span></div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-emerald-100 bg-emerald-50/30 p-5" data-testid="usdt-selector">
      <h3 className="text-base font-bold text-emerald-900">Pagar con USDT (TRC-20)</h3>
      <p className="text-xs text-emerald-700 mb-3">Mas barato que Stripe (~$0.10 de fee fijo). Activacion automatica.</p>
      {error && <div data-testid="usdt-error" className="text-sm text-red-700 bg-red-50 rounded p-2 mb-3">{error}</div>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {levels.map(l => (
          <button key={l.id} onClick={() => { setSelected(l.id); createOrder(l.id); }}
            disabled={loading}
            data-testid={`usdt-buy-${l.id}`}
            className={`p-3 rounded-lg border text-left ${selected === l.id ? 'border-emerald-500 bg-white' : 'border-emerald-200 bg-white hover:border-emerald-400'} disabled:opacity-50`}>
            <div className="text-xs font-semibold text-emerald-800">{l.name}</div>
            <div className="text-lg font-bold text-emerald-900">{l.price_usd} USDT</div>
            <div className="text-[10px] text-emerald-700">{l.cualcoin_included} Cc</div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default UsdtPaymentPanel;
